import React, { useState } from "react";
import styled, { createGlobalStyle, css } from "styled-components";

const GlobalStyle = createGlobalStyle`
  html {
    height: 100%
  }
  body {
    font-family: Arial, Helvetica, sans-serif;
    background: linear-gradient(to bottom, #f05053, #e1eec3);
    height: 100%;
    margin: 0;
    color: #555;
  }
`;

const sharedStyles = css`
  background-color: #eee;
  height: 40px;
  border-radius: 5px;
  border: 1px solid #ddd;
  margin: 10px 0 20px 0;
  padding: 20px;
  box-sizing: border-box;
`;

const StyledFormWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  padding: 0 20px;
`;

const StyledForm = styled.form`
  width: 100%;
  max-width: 700px;
  padding: 40px;
  background-color: #fff;
  border-radius: 10px;
  box-sizing: border-box;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.2);

  @media screen and (max-width: 280px) {
    padding: 20px;
  }
`;

const StyledInput = styled.input`
  display: block;
  width: 100%;
  ${sharedStyles}
`;

const StyledFieldset = styled.fieldset`
  display: flex;
  gap: 20px;
  border: none;
`;

const StyledButton = styled.button`
  display: block;
  background-color: #f7797d;
  color: #fff;
  font-size: 0.9rem;
  border: 0;
  border-radius: 5px;
  height: 40px;
  padding: 0 20px;
  cursor: pointer;
  box-sizing: border-box;

  @media screen and (max-width: 360px) {
    padding: 0 5px;
  }
`;

const StyledError = styled.div`
  color: red;
  font-weight: 800;
  margin: 0 0 40px 0;
`;

const roundToTwo = (num) => {
  return +(Math.round(num + "e+2") + "e-2");
};

const calcParityTokens = (received, parityRatio) => {
  const parity = received / parityRatio;
  const sellerTokens = roundToTwo(0.9 * parity - received);
  const customerTokens = roundToTwo(0.1 * parity);

  return {
    sellerTokens,
    customerTokens,
    parity,
  };
};

const initialOutput = {
  received: 0,
  sellerTokens: 0,
  customerTokens: 0,
  parity: 0,
  parityRatio: 0,
  calculated: false,
};

const initialInput = {
  received: "",
  parityRatio: "",
};

function App() {
  const [input, setInput] = useState(initialInput);
  const [output, setOutput] = useState(initialOutput);
  const [error, setError] = useState("");
  const [reset, setReset] = useState(false);

  const handleInput = (e) => {
    const inputName = e.currentTarget.name;
    const value = e.currentTarget.value;

    setInput((prev) => ({ ...prev, [inputName]: value }));
  };

  const handleReset = () => {
    setReset(true);
    setInput(initialInput);
    setOutput(initialOutput);
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log("handleSubmit reset ", reset);
    if (reset) {
      setReset(false);
      return;
    }

    if (input.received === "") {
      setError(`Provide amount received`);
      return;
    }

    if (input.parityRatio === "") {
      setError(`Provide parity ratio`);
      return;
    }

    const parityRatioNumeric = parseFloat(input.parityRatio) / 100;

    if (parityRatioNumeric > 1) {
      setError(`Provide parity ratio <= 100`);
      return;
    }
    const receivedNumeric = parseFloat(input.received);
    setError("");

    const { sellerTokens, customerTokens, parity } = calcParityTokens(
      receivedNumeric,
      parityRatioNumeric
    );

    setOutput({
      sellerTokens,
      customerTokens,
      received: receivedNumeric,
      parity,
      parityRatioNumeric,
      calculated: true,
    });
  };

  return (
    <>
      <GlobalStyle />
      <StyledFormWrapper>
        <StyledForm onSubmit={handleSubmit}>
          <h1>Parity Calculator</h1>
          <label htmlFor="received">Enter cash amount received</label>
          <StyledInput
            type="text"
            name="received"
            value={input.received}
            placeholder="Examples: 300.00, 25.25, 20, 0.95, .95"
            inputMode="decimal"
            pattern="^\s*?(\d+(\.\d{1,2})?|\.\d{1,2})\s*$"
            title="A number with up to 2 decimals and greater than 0. No commas"
            onChange={handleInput}
          />
          <label htmlFor="parityRatio">Enter parity ratio </label>
          <StyledInput
            type="text"
            name="parityRatio"
            value={input.parityRatio}
            placeholder="Example: 33.33 is 33.33%"
            inputMode="decimal"
            pattern="^\s*?(\d+(\.\d{1,2})?|\.\d{1,2})\s*$"
            title="A perdentage with 2 decimals and greater than 0"
            onChange={handleInput}
          />
          {error && (
            <StyledError>
              <p>{error}</p>
            </StyledError>
          )}
          <StyledFieldset>
            <StyledButton type="submit">Calculate Parity Tokens</StyledButton>
            <StyledButton onClick={handleReset}>Clear</StyledButton>
          </StyledFieldset>
          {output.calculated && (
            <div>
              <p>Parity: {output.parity.toFixed(2)} </p>
              <p>Seller Parity Tokens: {output.sellerTokens.toFixed(2)}</p>
              <p>Customer Parity Tokens: {output.customerTokens.toFixed(2)}</p>
            </div>
          )}
        </StyledForm>
      </StyledFormWrapper>
    </>
  );
}

export default App;
